import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  MenuCombo,
  MenuComboComponent,
} from "@/redux/services/menu/menu_combo.type";
import { ItemOption, MenuItem } from "@/redux/services/menu/menu.type";
import {
  ComboOptionChoices,
  comboChosenOptions,
  comboItemOptions,
  comboRequiredOptionsMet,
  toggleComboOptionValue,
} from "@/lib/comboOptions";
import { cn, displayPrice, formatSideType, getImageUrl } from "@/lib/utils";
import { Check, Minus, Plus } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

interface ComboSelection {
  componentId: string;
  menuItemId: string;
  menuItemTitle: string;
  sideType?: string;
  /** Chosen option value ids of the dish, in option order. */
  selectedOptionValueIds?: string[];
  /** Titles of those values, for the cart line. */
  selectedOptionLabels?: string[];
}

interface ComboConfigModalProps {
  open: boolean;
  onClose: () => void;
  combo: MenuCombo | null;
  currency: string;
  /**
   * Active menu dishes by placement id. The combo read does not embed the
   * dishes' options, so they are looked up here (SFRESTO-183).
   */
  menuItemsById: Record<string, MenuItem>;
  onAdd: (
    quantity: number,
    additionalDemands: string,
    selections: ComboSelection[]
  ) => void;
}

const ComboConfigModal: React.FC<ComboConfigModalProps> = ({
  open,
  onClose,
  combo,
  currency,
  menuItemsById,
  onAdd,
}) => {
  const [quantity, setQuantity] = useState(1);
  const [additionalDemands, setAdditionalDemands] = useState("");
  // selections keyed by componentId -> array of selected menu item ids
  const [selections, setSelections] = useState<
    Record<string, { menuItemId: string; sideType?: string }[]>
  >({});
  // option choices keyed by `${componentId}::${menuItemId}`
  const [optionChoices, setOptionChoices] = useState<
    Record<string, ComboOptionChoices>
  >({});

  const resetState = useCallback(() => {
    setQuantity(1);
    setAdditionalDemands("");
    setSelections({});
    setOptionChoices({});
  }, []);

  const choiceKey = (componentId: string, menuItemId: string) =>
    `${componentId}::${menuItemId}`;

  const clearChoices = (componentId: string, menuItemIds: string[]) => {
    if (menuItemIds.length === 0) return;
    setOptionChoices((prev) => {
      const next = { ...prev };
      for (const id of menuItemIds) delete next[choiceKey(componentId, id)];
      return next;
    });
  };

  const findComponentItem = (
    component: MenuComboComponent,
    menuItemId: string
  ): MenuItem | undefined =>
    [
      ...component.includedMenuItems,
      ...(component.menuSection?.menuItems ?? []),
    ].find((i) => i.id === menuItemId);

  const optionsFor = (
    component: MenuComboComponent,
    menuItemId: string
  ): ItemOption[] => {
    const item = findComponentItem(component, menuItemId);
    return item ? comboItemOptions(item, menuItemsById) : [];
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      resetState();
      onClose();
    }
  };

  // Get available items for a component
  const getAvailableItems = (component: MenuComboComponent): MenuItem[] => {
    const sectionItems = component.menuSection?.menuItems ?? [];
    const includedIds = component.includedMenuItems.map((i) => i.id);
    const excludedIds = component.excludedMenuItems.map((i) => i.id);

    let items: MenuItem[];

    if (includedIds.length > 0) {
      // Only included items
      items = component.includedMenuItems;
    } else if (sectionItems.length > 0) {
      // All section items except excluded
      items = sectionItems.filter((item) => !excludedIds.includes(item.id));
    } else {
      items = [];
    }

    return items.filter((item) => item.isAvailable);
  };

  const toggleSelection = (
    componentId: string,
    menuItemId: string,
    maxChoiceCount: number,
    sideType?: string
  ) => {
    setSelections((prev) => {
      const current = prev[componentId] ?? [];
      const existingIndex = current.findIndex(
        (s) => s.menuItemId === menuItemId
      );

      if (existingIndex >= 0) {
        // Deselect
        clearChoices(componentId, [menuItemId]);
        return {
          ...prev,
          [componentId]: current.filter((_, i) => i !== existingIndex),
        };
      }

      if (maxChoiceCount === 1) {
        // Single choice: replace
        clearChoices(
          componentId,
          current.map((s) => s.menuItemId)
        );
        return {
          ...prev,
          [componentId]: [{ menuItemId, sideType }],
        };
      }

      if (maxChoiceCount > 0 && current.length >= maxChoiceCount) {
        return prev;
      }

      return {
        ...prev,
        [componentId]: [...current, { menuItemId, sideType }],
      };
    });
  };

  const toggleOption = (
    componentId: string,
    menuItemId: string,
    itemOption: ItemOption,
    valueId: string
  ) => {
    const key = choiceKey(componentId, menuItemId);
    setOptionChoices((prev) => ({
      ...prev,
      [key]: toggleComboOptionValue(prev[key] ?? {}, itemOption, valueId),
    }));
  };

  const selectOption = (
    componentId: string,
    menuItemId: string,
    itemOptionId: string,
    valueId: string
  ) => {
    const key = choiceKey(componentId, menuItemId);
    setOptionChoices((prev) => ({
      ...prev,
      [key]: { ...(prev[key] ?? {}), [itemOptionId]: valueId ? [valueId] : [] },
    }));
  };

  const requiredMet = useMemo(() => {
    if (!combo) return false;
    const componentsMet = combo.components
      .filter((c) => c.isRequired)
      .every((c) => (selections[c.id]?.length ?? 0) > 0);
    if (!componentsMet) return false;

    // The API rejects a dish missing one of its required options.
    return combo.components.every((component) =>
      (selections[component.id] ?? [])
        .filter((sel) => !sel.sideType)
        .every((sel) =>
          comboRequiredOptionsMet(
            optionsFor(component, sel.menuItemId),
            optionChoices[choiceKey(component.id, sel.menuItemId)]
          )
        )
    );
  }, [combo, selections, optionChoices, menuItemsById]);

  const handleAdd = () => {
    if (!combo || !requiredMet) return;

    const comboSelections: ComboSelection[] = [];
    for (const component of combo.components) {
      const selected = selections[component.id] ?? [];
      for (const sel of selected) {
        const menuItem = findComponentItem(component, sel.menuItemId);
        const chosen = sel.sideType
          ? { valueIds: [], labels: [] }
          : comboChosenOptions(
              optionsFor(component, sel.menuItemId),
              optionChoices[choiceKey(component.id, sel.menuItemId)]
            );

        comboSelections.push({
          componentId: component.id,
          menuItemId: sel.menuItemId,
          menuItemTitle: menuItem?.title ?? "",
          sideType: sel.sideType,
          selectedOptionValueIds:
            chosen.valueIds.length > 0 ? chosen.valueIds : undefined,
          selectedOptionLabels:
            chosen.labels.length > 0 ? chosen.labels : undefined,
        });
      }
    }

    onAdd(quantity, additionalDemands.trim(), comboSelections);
    resetState();
    onClose();
  };

  if (!combo) return null;

  const sortedComponents = [...combo.components].sort(
    (a, b) => a.sortOrder - b.sortOrder
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex flex-col p-0 gap-0 sm:max-w-lg">
        <DialogHeader className="p-5 pb-0">
          <div className="flex items-center gap-4">
            <img
              src={getImageUrl(combo.image?.url)}
              alt={combo.name}
              className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
            />
            <div className="min-w-0">
              <DialogTitle className="text-base truncate">
                {combo.name}
              </DialogTitle>
              <DialogDescription className="text-sm font-semibold text-primary mt-0.5">
                {displayPrice(combo.price, currency)}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Components selection */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {sortedComponents.map((component) => {
            const availableItems = getAvailableItems(component);
            const hasSideTypes = component.allowedSideTypes.length > 0;
            const selected = selections[component.id] ?? [];

            return (
              <div key={component.id} className="space-y-2">
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-semibold">{component.name}</h4>
                  {component.isRequired && (
                    <span className="text-[10px] font-semibold bg-destructive/10 text-destructive px-2 py-0.5 rounded-full">
                      Obligatoire
                    </span>
                  )}
                  {component.maxChoiceCount > 1 && (
                    <span className="text-[10px] text-muted-foreground">
                      (max {component.maxChoiceCount})
                    </span>
                  )}
                </div>

                {component.description && (
                  <p className="text-xs text-muted-foreground">
                    {component.description}
                  </p>
                )}

                {/* Side type selection */}
                {hasSideTypes && (
                  <div className="space-y-1">
                    {component.allowedSideTypes.map((sideType) => {
                      const isSelected = selected.some(
                        (s) => s.sideType === sideType
                      );
                      return (
                        <button
                          key={sideType}
                          onClick={() =>
                            toggleSelection(
                              component.id,
                              `side::${sideType}`,
                              component.maxChoiceCount,
                              sideType
                            )
                          }
                          className={cn(
                            "w-full flex items-center justify-between p-3 rounded-lg border text-sm transition-all",
                            isSelected
                              ? "border-primary bg-primary/5"
                              : "border-border hover:border-primary/30"
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <div
                              className={cn(
                                "w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors",
                                isSelected
                                  ? "border-primary bg-primary"
                                  : "border-border"
                              )}
                            >
                              {isSelected && (
                                <Check size={12} className="text-white" />
                              )}
                            </div>
                            <span className={isSelected ? "font-medium" : ""}>
                              {formatSideType(sideType)}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Menu item selection */}
                {availableItems.length > 0 && (
                  <div className="space-y-1">
                    {availableItems.map((menuItem) => {
                      const isSelected = selected.some(
                        (s) => s.menuItemId === menuItem.id
                      );
                      const itemOptions = isSelected
                        ? optionsFor(component, menuItem.id)
                        : [];
                      const choices =
                        optionChoices[choiceKey(component.id, menuItem.id)] ??
                        {};
                      return (
                        <div key={menuItem.id}>
                          <button
                            onClick={() =>
                              toggleSelection(
                                component.id,
                                menuItem.id,
                                component.maxChoiceCount
                              )
                            }
                            className={cn(
                              "w-full flex items-center justify-between p-3 rounded-lg border text-sm transition-all",
                              isSelected
                                ? "border-primary bg-primary/5"
                                : "border-border hover:border-primary/30"
                            )}
                          >
                            <div className="flex items-center gap-2">
                              <div
                                className={cn(
                                  "w-5 h-5 border-2 flex items-center justify-center flex-shrink-0 transition-colors",
                                  component.maxChoiceCount === 1
                                    ? "rounded-full"
                                    : "rounded",
                                  isSelected
                                    ? "border-primary bg-primary"
                                    : "border-border"
                                )}
                              >
                                {isSelected && (
                                  <Check size={12} className="text-white" />
                                )}
                              </div>
                              <img
                                src={getImageUrl(menuItem.image?.url)}
                                alt={menuItem.title}
                                className="w-8 h-8 rounded-full object-cover"
                              />
                              <span className={isSelected ? "font-medium" : ""}>
                                {menuItem.title}
                              </span>
                            </div>
                          </button>
                          {itemOptions.length > 0 && (
                            <div className="ml-7 mt-1 mb-2 pl-3 border-l-2 border-primary/20 space-y-3">
                              {itemOptions.map((itemOption) => {
                                const option = itemOption.menuItemOption;
                                const values =
                                  option.menuItemOptionValues ?? [];
                                const selectedVals =
                                  choices[itemOption.id] ?? [];
                                return (
                                  <div
                                    key={itemOption.id}
                                    className="space-y-1"
                                  >
                                    <div className="flex items-center gap-2">
                                      <h5 className="text-xs font-semibold">
                                        {option.title}
                                      </h5>
                                      {itemOption.isRequired && (
                                        <span className="text-[9px] font-semibold bg-destructive/10 text-destructive px-1.5 py-0.5 rounded-full">
                                          Obligatoire
                                        </span>
                                      )}
                                      {option.type === "checkbox" &&
                                        itemOption.maxSelections > 1 && (
                                          <span className="text-[9px] text-muted-foreground">
                                            (max {itemOption.maxSelections})
                                          </span>
                                        )}
                                    </div>
                                    {option.type === "select" ? (
                                      <Select
                                        value={selectedVals[0] ?? ""}
                                        onValueChange={(val) =>
                                          selectOption(
                                            component.id,
                                            menuItem.id,
                                            itemOption.id,
                                            val
                                          )
                                        }
                                      >
                                        <SelectTrigger className="h-8 text-xs">
                                          <SelectValue placeholder="Choisir" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {values.map((value) => (
                                            <SelectItem
                                              key={value.id}
                                              value={value.id}
                                            >
                                              {value.title}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    ) : (
                                      <div className="space-y-0.5">
                                        {values.map((value) => {
                                          const isValSelected =
                                            selectedVals.includes(value.id);
                                          return (
                                            <button
                                              key={value.id}
                                              type="button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                toggleOption(
                                                  component.id,
                                                  menuItem.id,
                                                  itemOption,
                                                  value.id
                                                );
                                              }}
                                              className={cn(
                                                "w-full flex items-center gap-1.5 p-2 rounded-md border text-xs transition-all",
                                                isValSelected
                                                  ? "border-primary bg-primary/5"
                                                  : "border-border hover:border-primary/30"
                                              )}
                                            >
                                              <div
                                                className={cn(
                                                  "w-4 h-4 border-2 flex items-center justify-center flex-shrink-0 transition-colors",
                                                  option.type === "radio"
                                                    ? "rounded-full"
                                                    : "rounded-sm",
                                                  isValSelected
                                                    ? "border-primary bg-primary"
                                                    : "border-border"
                                                )}
                                              >
                                                {isValSelected && (
                                                  <Check
                                                    size={10}
                                                    className="text-white"
                                                  />
                                                )}
                                              </div>
                                              <span
                                                className={
                                                  isValSelected
                                                    ? "font-medium"
                                                    : ""
                                                }
                                              >
                                                {value.title}
                                              </span>
                                            </button>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {availableItems.length === 0 && !hasSideTypes && (
                  <p className="text-xs text-muted-foreground italic">
                    Aucun élément disponible
                  </p>
                )}
              </div>
            );
          })}

          {/* Notes */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold">Notes</h4>
            <textarea
              value={additionalDemands}
              onChange={(e) => setAdditionalDemands(e.target.value)}
              placeholder="Instructions spéciales..."
              rows={2}
              className="w-full rounded-lg border border-border p-3 text-sm placeholder:text-muted-foreground outline-none focus:border-primary transition-colors resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-border space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Quantité</span>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                className="w-8 h-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors"
              >
                <Minus size={16} />
              </button>
              <span className="text-sm font-semibold w-6 text-center">
                {quantity}
              </span>
              <button
                onClick={() => setQuantity((q) => q + 1)}
                className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 transition-colors"
              >
                <Plus size={16} />
              </button>
            </div>
          </div>

          <Button
            onClick={handleAdd}
            disabled={!requiredMet}
            className="w-full"
            size="lg"
          >
            Ajouter — {displayPrice(combo.price * quantity, currency)}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export type { ComboSelection };
export default ComboConfigModal;
